import React from 'react';
import type {RefOption} from './types';

/**
 * Примитивы полей формы вещи. Вынесены, чтобы классы токенов
 * (`field.*`, `form.rowGap`) жили в одном месте, а секции описывали только
 * состав полей — раньше один и тот же набор классов был скопирован 20+ раз.
 *
 * Локатор поля (`data-testid`) равен его `id` — конвенция docs/testing/locators.md:
 * два разных имени у одного поля расходятся при первой же правке, поэтому здесь
 * одно, и проставляется оно централизованно, а не в каждой секции.
 */

const LABEL_CLASS = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const ERROR_CLASS = 'mt-1 text-sm text-red-600 dark:text-red-400';

/** Сетка коротких связанных полей: 1 колонка на мобиле, 2 на десктопе (styleguide §Forms). */
export const FieldRow: React.FC<{children: React.ReactNode; cols?: 2 | 3}> = ({children, cols = 2}) => (
    <div className={`grid grid-cols-1 gap-4 ${cols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {children}
    </div>
);

export interface SelectFieldProps {
    id: string;
    label: string;
    value: string;
    options: RefOption[];
    onChange: (value: string) => void;
}

export const SelectField: React.FC<SelectFieldProps> = ({id, label, value, options, onChange}) => (
    <div>
        <label htmlFor={id} className={LABEL_CLASS}>{label}</label>
        <select id={id} data-testid={id} value={value} onChange={e => onChange(e.target.value)} className="input-field">
            <option value="">—</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
        </select>
    </div>
);

export interface TextFieldProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    type?: 'text' | 'number' | 'date';
    required?: boolean;
    placeholder?: string;
    error?: string;
    min?: string;
    step?: string;
    /** Enter в числовом поле не должен отправлять форму раньше времени. */
    blockEnter?: boolean;
}

export const TextField: React.FC<TextFieldProps> = ({
    id, label, value, onChange, type = 'text', required, placeholder, error, min, step, blockEnter
}) => (
    <div>
        <label htmlFor={id} className={LABEL_CLASS}>
            {label}{required && ' *'}
        </label>
        <input
            id={id}
            data-testid={id}
            type={type}
            value={value}
            min={min}
            step={step}
            required={required}
            placeholder={placeholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={e => onChange(e.target.value)}
            onKeyDown={blockEnter ? (e => { if (e.key === 'Enter') e.preventDefault(); }) : undefined}
            className={`input-field ${error ? 'border-red-400 dark:border-red-500' : ''}`}
        />
        {error && <p id={`${id}-error`} className={ERROR_CLASS}>{error}</p>}
    </div>
);

export interface CheckboxFieldProps {
    id: string;
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({id, label, checked, onChange}) => (
    <label htmlFor={id} className="flex items-center gap-2 min-h-[44px] cursor-pointer">
        <input
            id={id}
            data-testid={id}
            type="checkbox"
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            className="rounded"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
    </label>
);

export interface ChipGroupProps {
    legend: string;
    options: RefOption[];
    selected: string[];
    onToggle: (id: string) => void;
    /** Локатор группы (`item-chips-colors`); чип внутри — `<testId>-<optionId>`. */
    testId: string;
}

/**
 * Multi-select независимых значений (цвета, сезоны, поводы). Чипы — кнопки с
 * `aria-pressed`, обёрнутые в `fieldset/legend`: группа читается скринридером
 * целиком, а не как набор безымянных кнопок.
 */
export const ChipGroup: React.FC<ChipGroupProps> = ({legend, options, selected, onToggle, testId}) => {
    if (options.length === 0) {
        return null;
    }
    return (
        <fieldset data-testid={testId}>
            <legend className={LABEL_CLASS}>{legend}</legend>
            <div className="flex flex-wrap gap-2">
                {options.map(o => {
                    const active = selected.includes(o.id);
                    return (
                        <button
                            key={o.id}
                            type="button"
                            data-testid={`${testId}-${o.id}`}
                            aria-pressed={active}
                            onClick={() => onToggle(o.id)}
                            className={`px-3 py-1.5 min-h-[36px] rounded-full text-sm border pressable ${
                                active
                                    ? 'bg-primary-100 dark:bg-primary-900 border-primary-500 text-primary-700 dark:text-primary-300'
                                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                            {o.nameRu}
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
};
